import React, { useState, useMemo } from "react";
import { Check, ShoppingCart, Plus } from "lucide-react";
import { baseCourses, endorsementCourses, COURSE_PRICE, ENDORSEMENT_PRICE } from "../mock";
import { goToCheckout } from "../lib/checkout";

// Interactive course builder. Pick ONE base course + optional endorsements,
// see a running total, then "Checkout" links to the external app.cdlacademy.com.
export default function CourseSelector({ id = "select-courses" }) {
  const [base, setBase] = useState(null);
  const [addons, setAddons] = useState([]);

  const toggleAddon = (aid) =>
    setAddons((prev) =>
      prev.includes(aid) ? prev.filter((x) => x !== aid) : [...prev, aid]
    );

  const selectedIds = useMemo(
    () => [base, ...addons].filter(Boolean),
    [base, addons]
  );

  const selectedCourses = useMemo(
    () =>
      [...baseCourses, ...endorsementCourses].filter((c) =>
        selectedIds.includes(c.id)
      ),
    [selectedIds]
  );

  const total = useMemo(
    () => selectedCourses.reduce((sum, c) => sum + c.price, 0),
    [selectedCourses]
  );

  return (
    <section id={id} className="relative py-24 bg-[#F4F7FB]">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-14">
          <span className="eyebrow">Build Your Enrollment</span>
          <h2 className="font-display text-4xl md:text-5xl text-brand mt-3">
            Select Your Courses
          </h2>
          <p className="text-slate-500 mt-4 max-w-xl mx-auto">
            Choose your license class, then stack on any endorsements you need.
            Every course is <span className="text-brand font-semibold">100% online</span> and
            self-paced — classes are{" "}
            <span className="text-brand font-semibold">${COURSE_PRICE}</span>{" "}
            and endorsements just <span className="text-brand font-semibold">${ENDORSEMENT_PRICE}</span>.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Selection area */}
          <div className="lg:col-span-2 space-y-8">
            {/* Base courses */}
            <div>
              <h3 className="font-head font-semibold uppercase tracking-wider text-slate-500 text-sm mb-4">
                Step 1 — Choose a class <span className="text-slate-400">(optional — you can buy endorsements on their own)</span>
              </h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {baseCourses.map((c) => {
                  const active = base === c.id;
                  const Icon = c.icon;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setBase(c.id)}
                      className={`text-left p-5 rounded-xl border-2 transition-all duration-200 relative bg-white ${
                        active
                          ? "border-brand shadow-[0_16px_30px_-18px_rgba(22,54,92,0.5)]"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      {active && (
                        <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-brand flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" strokeWidth={3} />
                        </div>
                      )}
                      <Icon
                        className={`w-7 h-7 mb-3 ${active ? "text-brand" : "text-steel"}`}
                      />
                      <div className="font-head font-semibold text-slate-900 text-base">
                        {c.name}
                      </div>
                      <div className="text-slate-400 text-xs mt-1">{c.tagline}</div>
                      <div className="text-brand font-bold mt-3">${c.price}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Endorsements */}
            <div>
              <h3 className="font-head font-semibold uppercase tracking-wider text-slate-500 text-sm mb-4">
                Step 2 — Add endorsements <span className="text-slate-400">(optional)</span>
              </h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {endorsementCourses.map((c) => {
                  const active = addons.includes(c.id);
                  const Icon = c.icon;
                  return (
                    <button
                      key={c.id}
                      onClick={() => toggleAddon(c.id)}
                      className={`text-left p-5 rounded-xl border-2 transition-all duration-200 relative bg-white ${
                        active
                          ? "border-brand shadow-[0_16px_30px_-18px_rgba(22,54,92,0.5)]"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors"
                        style={{
                          background: active ? "#16365C" : "transparent",
                          borderColor: active ? "#16365C" : "#CBD5E1",
                        }}
                      >
                        {active ? (
                          <Check className="w-4 h-4 text-white" strokeWidth={3} />
                        ) : (
                          <Plus className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <Icon
                        className={`w-7 h-7 mb-3 ${active ? "text-brand" : "text-steel"}`}
                      />
                      <div className="font-head font-semibold text-slate-900 text-base">
                        {c.name}
                      </div>
                      <div className="text-slate-400 text-xs mt-1">{c.tagline}</div>
                      <div className="text-brand font-bold mt-3">+${c.price}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Summary card */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:sticky lg:top-24 soft-shadow">
              <h3 className="font-head font-semibold uppercase tracking-wider text-slate-900 text-sm mb-5">
                Your Enrollment
              </h3>

              {selectedIds.length === 0 ? (
                <p className="text-slate-400 text-sm py-6 text-center">
                  Select a class to get started.
                </p>
              ) : (
                <ul className="space-y-3 mb-5">
                  {selectedCourses.map((c) => (
                    <li
                      key={c.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-slate-700">{c.name}</span>
                      <span className="text-slate-500">${c.price}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
                <span className="font-head font-semibold uppercase tracking-wide text-slate-500 text-sm">
                  Total
                </span>
                <span className="font-display text-3xl text-brand">${total.toFixed(2)}</span>
              </div>

              <button
                disabled={selectedIds.length === 0}
                onClick={() => goToCheckout(selectedIds)}
                className={`w-full mt-6 rounded-md py-3.5 flex items-center justify-center gap-2 ${
                  selectedIds.length > 0
                    ? "btn-brand"
                    : "bg-slate-200 text-slate-400 cursor-not-allowed font-head font-semibold uppercase tracking-wide"
                }`}
              >
                <ShoppingCart className="w-5 h-5" />
                {selectedIds.length > 0 ? "Proceed to Checkout" : "Select a course"}
              </button>
              <p className="text-slate-400 text-[11px] text-center mt-3 leading-relaxed">
                Secure checkout &amp; payment handled at app.cdlacademy.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
